<template>
  <custom-modal
    v-model="showDialog"
    :max-width="1200"
    :add-card-dismiss="true"
    @close="showDialog = false"
  >
    <template #title>
      <span>{{ name }}</span>
    </template>
    <template #content>
      <base-row v-if="!showLinkAdAccountView">
        <base-col cols="12" class="mt-2">
          <base-row justify="end">
            <base-button
              color="primary"
              @click="showLinkAdAccountView = true"
              >{{ $t('messages.linkAdAccount') }}</base-button
            >
          </base-row>
        </base-col>
        <base-col cols="12">
          <base-row>
            <BaseDataTable
              :headers="headers"
              :items="linkedAdAccount"
              :items-per-page-options="pageSizeOptions"
            >
              <template #item="{ item }">
                <tr>
                  <td class="text-center">
                    <base-icon
                      :icon="channeInfo[item.searchEngineTypeId]?.icon"
                    />
                  </td>
                  <td class="text-center">{{ item.accountId }}</td>
                  <td class="text-center">{{ item.accountName }}</td>
                  <td class="text-center">
                    {{ ProcessingStatusName[item.processingStatus] ?? '-' }}
                  </td>
                  <td class="text-center">
                    {{
                      item.lastUploadAt
                        ? getBusinessProfileDate(
                            item.lastUploadAt,
                            bpOffset
                          ).toLocaleDateString('en-US', {
                            timeZone: 'UTC',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric'
                          })
                        : '-'
                    }}
                  </td>
                </tr>
              </template>
            </BaseDataTable>
          </base-row>
        </base-col>
      </base-row>
      <base-select
        v-else
        v-model="selectedAccountIds"
        :label="$t('messages.selectAdAccount')"
        :items="availableAdAccount"
        item-title="name"
        item-value="value"
        multiple
      />
    </template>
    <template v-if="showLinkAdAccountView" #action-button>
      <base-button class="bg-white" @click="showLinkAdAccountView = false">{{
        $t('labels.cancel')
      }}</base-button>
      <base-button
        color="primary"
        :disabled="isSaveButtonDisable"
        @click="handleSave"
        >{{ $t('labels.save') }}</base-button
      >
    </template>
  </custom-modal>
</template>

<script setup lang="ts">
  import { getBusinessProfileDate } from '@/helpers/dateHelper'
  import { ref, computed, watch, onMounted } from 'vue'
  import CustomModal from '@/components/customComponents/CustomModal.vue'
  import {
    BaseRow,
    BaseCol,
    BaseSelect,
    BaseIcon,
    BaseButton,
    BaseDataTable
  } from '@deltax/vuetify-wrapper-components'
  import { channels, channeInfo } from '@/constants/Channel'
  import AccountService from '@/services/accountService'
  import { useAppStore } from '@/store/app'
  import { BpseSegmentInfo } from '@/types/customerSegmentResponse'
  import { AccountResponse } from '@/types/AccountResponse'
  import { ProcessingStatusName } from '../Constant/ProcessingStatus'
  import pageSizeOptions from '@/constants/itemsPerPage'

  // constant
  const headers = [
    { title: 'Channel', key: 'channel', align: 'center', sortable: false },
    { title: 'Account Id', key: 'accountId', align: 'center', sortable: false },
    {
      title: 'Account Name',
      key: 'accountName',
      align: 'center',
      sortable: false
    },
    { title: 'Status', key: 'status', align: 'center', sortable: false },
    {
      title: 'Last uploaded At',
      key: 'lastUploadedAt',
      align: 'center',
      sortable: false
    }
  ]

  const appStore = useAppStore()
  const bpOffset = computed(
    () => appStore.getSelectedBusinessProfileTimezone?.offset ?? 0
  )
  const accountService = new AccountService()

  interface Props {
    modelValue: boolean
    linkedAdAccount: Array<BpseSegmentInfo>
    name: string
    segmentId: number
  }
  const props = defineProps<Props>()
  const emit = defineEmits(['fetch-segments', 'update:modelValue'])

  watch(
    () => props.modelValue,
    val => {
      showDialog.value = val
    }
  )

  const showDialog = ref(false)
  watch(showDialog, val => {
    emit('update:modelValue', val)
    if (!val) {
      showLinkAdAccountView.value = false
    }
  })

  let showLinkAdAccountView = ref(false)
  watch(showLinkAdAccountView, val => {
    if (val) {
      fetchAdAccount()
    }
  })

  let availableAdAccount = ref<Array<{ name: string; value: number }>>([])
  const fetchAdAccount = async () => {
    if (appStore.getSelectedBusinessProfile) {
      const allAccounts: AccountResponse[] =
        await accountService.getAccountDetails(
          appStore.getSelectedBusinessProfile.id,
          channels.FaceBook
        )
      availableAdAccount.value = allAccounts
        .filter(
          account =>
            !props.linkedAdAccount.some(
              lac => lac.accountId === account.accountId
            ) && account.searchEngineTypeId !== channels.LeadX
        )
        .map(account => ({ name: account.accountNickName, value: account.id }))
    }
  }

  onMounted(() => {
    showDialog.value = props.modelValue
  })

  const selectedAccountIds = ref<Array<number>>([])
  const isSaveButtonDisable = computed(
    () => selectedAccountIds.value.length <= 0
  )
  const handleSave = async () => {
    if (appStore.getSelectedBusinessProfile) {
      await accountService.createSegmentBpseMapping(
        appStore.getSelectedBusinessProfile.id,
        props.segmentId,
        selectedAccountIds.value
      )
      selectedAccountIds.value = []
      showDialog.value = false
      emit('fetch-segments')
      showLinkAdAccountView.value = false
    }
  }
</script>
